/**
 * QuizPage.tsx — Intake quiz, AI-powered wrapper routing
 * Route: /start
 * Asks what user is trying to do, routes to correct wrapper
 */
import { useState } from "react";

const QUICK_OPTIONS = [
  {
    id: "director",
    label: "I received a citation or inspection finding",
    sub: "From HHSC, a licensing agency, or a state inspector",
    url: "/for-directors",
    cta: "Go to Director's Defense",
    headline: "Director's Defense is built for exactly this.",
    expect: "Paste any sentence from your inspection report. The tool breaks it down in plain language — what it claims, what it assumes, and how to respond. Export your analysis as a document you can hand to your attorney.",
  },
  {
    id: "mirror",
    label: "I want to understand my own thinking patterns",
    sub: "Personal reflection, emotional clarity, self-examination",
    url: "/mirror-bloom",
    cta: "Go to Mirror Bloom Logic",
    headline: "Mirror Bloom Logic trains the eye that sees.",
    expect: "A 30-day cognitive precision training path. You'll learn to name what you already sense — the hidden assumptions in language, the patterns in how people argue, the structure underneath what's said.",
  },
  {
    id: "naming",
    label: "I'm naming a product, brand, or company",
    sub: "Trademark research, linguistic grounding, USPTO viability",
    url: "/naming-roots",
    cta: "Go to Naming Roots Lab",
    headline: "Naming Roots Lab builds names from semantic bedrock.",
    expect: "Enter any word or concept. Get a full root analysis — linguistic origin, semantic family, USPTO viability rating, domain availability, and 2-4 naming candidates grounded in ancient structure.",
  },
  {
    id: "general",
    label: "I want to improve my writing and arguments",
    sub: "Documents, essays, emails, formal language",
    url: "/app",
    cta: "Open Logic Rigor",
    headline: "Logic Rigor trains the eye that reads structure.",
    expect: "Analyze any sentence across seven formal steps — decomposition, operators, quantifiers, modal claims, inference structure, validity, and failure modes. The vocabulary ledger grows with every session.",
  },
  {
    id: "roots",
    label: "I'm learning Hebrew or exploring language roots",
    sub: "Morphology, trilateral roots, semantic architecture",
    url: "/roots",
    cta: "Go to Roots Mode ℵ",
    headline: "Roots Mode ℵ maps living linguistic structure.",
    expect: "Enter any Hebrew word or transliteration. Get the three-letter root, the verb template (binyan), the semantic family, and the logical parallel. The oldest systematic semantic architecture on record.",
  },
];

const WORKER_URL = "https://logic-rigor-api.ellari.workers.dev";

const C = {
  bg: "#fdfaf6", white: "#fff", text: "#1c1814", muted: "#6a6058",
  amber: "#8a5a00", border: "#e4ddd6", borderLight: "#ede9e4",
  soft: "#f8f4ef", amberLight: "#f5ead8", amberBorder: "#d4b070",
};

interface Rec {
  wrapper: string; confidence: string; headline: string;
  what_to_expect: string; cta: string; url: string;
}

const OPT_MAP: Record<string, typeof QUICK_OPTIONS[0]> = Object.fromEntries(
  QUICK_OPTIONS.map(o => [o.id, o])
);

export default function QuizPage() {
  const [selected, setSelected] = useState<typeof QUICK_OPTIONS[0] | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState<Rec | null>(null);
  const [mode, setMode] = useState<"buttons" | "ai">("buttons");

  const recommend = async () => {
    if (!input.trim()) return;
    setLoading(true);
    setRec(null);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: `You are a program advisor for Logic Rigor, a cognitive precision training platform. Read the user's situation and recommend exactly one program. Return ONLY valid JSON, no markdown.

Programs:
- director: For new daycare directors or operators who received citations/findings from HHSC or state licensing. Plain language focus.
- inspection: For experienced operators managing active regulatory defense situations, appeals, admin reviews.
- mirror: Personal reflection, emotional clarity, self-examination, relationship patterns.
- naming: Founders, attorneys, brand strategists researching product/company names, trademark work.
- general: Writers, analysts, knowledge workers improving logic and writing.
- roots: Hebrew language learners, linguistic root systems, morphology.

Routing signals:
- Citation, finding, HHSC, licensing, childcare, daycare, inspector + new/confused → director
- Citation, finding, appeal, admin review, experienced → inspection  
- Self-reflection, emotions, patterns, relationships → mirror
- Naming, trademark, brand, IP, USPTO, domain → naming
- Hebrew, roots, morphology, language → roots
- Default → general

Return: {"wrapper":"...","confidence":"high|medium","headline":"one sentence why this fits","what_to_expect":"2-3 sentences about what they'll get","cta":"button text","url":"/for-directors|/inspection-defense|/mirror-bloom|/naming-roots|/app|/roots"}`,
          messages: [{ role: "user", content: input }],
        }),
      });
      const data = await res.json() as { content?: { type: string; text?: string }[] };
      const raw = data.content?.find(b => b.type === "text")?.text || "{}";
      const parsed: Rec = JSON.parse(raw.replace(/```json|```/g, "").trim().match(/\{[\s\S]*\}/)?.[0] || "{}");
      setRec(parsed);
    } catch {
      // Fallback to general
      setRec({ wrapper: "general", confidence: "medium", headline: "Logic Rigor is the right starting point.", what_to_expect: "Analyze any sentence for logical structure. Seven steps, named fallacies, a vocabulary that grows with every session.", cta: "Open Logic Rigor", url: "/app" });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "Georgia,'Times New Roman',serif" }}>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        .mono { font-family:'Courier New',monospace; }
        .btn-amber { display:inline-block; padding:13px 28px; background:${C.amber}; color:#fdfaf6; font-family:'Courier New',monospace; font-size:13px; letter-spacing:.06em; cursor:pointer; border:none; text-decoration:none; transition:background .2s; }
        .btn-amber:hover { background:#6a4400; }
        .btn-outline { display:inline-block; padding:11px 22px; background:transparent; color:${C.text}; font-family:'Courier New',monospace; font-size:12px; letter-spacing:.06em; cursor:pointer; border:1px solid ${C.border}; text-decoration:none; transition:all .2s; }
        .btn-outline:hover { border-color:${C.amber}; }
        .opt-card { padding:18px 20px; border:1px solid ${C.border}; background:${C.white}; cursor:pointer; transition:all .2s; text-align:left; width:100%; margin-bottom:8px; }
        .opt-card:hover { border-color:${C.amber}; background:${C.amberLight}; }
        .opt-card.active { border-color:${C.amber}; background:${C.amberLight}; }
        .spin { display:inline-block; width:13px; height:13px; border:2px solid ${C.border}; border-top-color:${C.amber}; border-radius:50%; animation:sp .7s linear infinite; }
        @keyframes sp { to { transform:rotate(360deg) } }
        .rec-card { border:1px solid ${C.amberBorder}; background:${C.amberLight}; padding:28px; margin-top:24px; }
        .tab { padding:8px 16px; font-family:'Courier New',monospace; font-size:12px; cursor:pointer; border:none; background:transparent; letter-spacing:.06em; border-bottom:2px solid transparent; transition:all .15s; }
        .tab.active { border-bottom-color:${C.amber}; color:${C.amber}; }
        .tab.inactive { color:${C.muted}; }
      `}</style>

      {/* Nav */}
      <nav style={{ borderBottom: `1px solid ${C.border}`, padding: "0 48px", height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", background: C.white }}>
        <a href="/" style={{ fontSize: 16, textDecoration: "none", color: C.text }}>Logic Rigor</a>
        <div style={{ display: "flex", gap: 24 }}>
          {[["For Directors", "/for-directors"], ["Mirror Bloom", "/mirror-bloom"], ["All programs", "/"]].map(([l, h]) => (
            <a key={l} href={h} className="mono" style={{ fontSize: 11, color: C.muted, textDecoration: "none" }}>{l}</a>
          ))}
        </div>
      </nav>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "80px 48px" }}>
        <p className="mono" style={{ fontSize: 10, color: C.muted, letterSpacing: ".14em", marginBottom: 20 }}>FIND YOUR PROGRAM</p>
        <h1 style={{ fontSize: "clamp(28px,4vw,44px)", fontWeight: 400, lineHeight: 1.2, marginBottom: 16 }}>
          What kind of pattern are you trying to catch?
        </h1>
        <p style={{ fontSize: 16, color: C.muted, lineHeight: 1.7, marginBottom: 40 }}>
          Pick the option that fits, or describe your situation and we'll point you in the right direction.
        </p>

        {/* Tab toggle */}
        <div style={{ borderBottom: `1px solid ${C.border}`, marginBottom: 32, display: "flex" }}>
          <button className={`tab ${mode === "buttons" ? "active" : "inactive"}`} onClick={() => { setMode("buttons"); setSelected(null); setRec(null); }}>Quick select</button>
          <button className={`tab ${mode === "ai" ? "active" : "inactive"}`} onClick={() => { setMode("ai"); setSelected(null); setRec(null); }}>Describe your situation</button>
        </div>

        {/* Quick select */}
        {mode === "buttons" && (
          <div>
            {QUICK_OPTIONS.map(opt => (
              <button key={opt.id} className={`opt-card ${selected?.id === opt.id ? "active" : ""}`}
                onClick={() => setSelected(selected?.id === opt.id ? null : opt)}>
                <div style={{ fontSize: 16, marginBottom: 4 }}>{opt.label}</div>
                <div className="mono" style={{ fontSize: 11, color: C.muted, letterSpacing: ".04em" }}>{opt.sub}</div>
              </button>
            ))}

            {selected && (
              <div className="rec-card">
                <p className="mono" style={{ fontSize: 10, color: C.amber, letterSpacing: ".1em", marginBottom: 12 }}>RECOMMENDED PROGRAM</p>
                <h3 style={{ fontSize: 20, fontWeight: 400, marginBottom: 12 }}>{selected.headline}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.75, color: C.muted, marginBottom: 24 }}>{selected.expect}</p>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <a href={selected.url} className="btn-amber">{selected.cta} →</a>
                  <button className="btn-outline" onClick={() => setSelected(null)} style={{ fontSize: 11 }}>See all programs</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI mode */}
        {mode === "ai" && (
          <div>
            <textarea value={input} onChange={e => { setInput(e.target.value); setRec(null); }}
              placeholder="Describe your situation in plain language — what's happening, what you need, what you've tried..."
              rows={5}
              style={{ width: "100%", padding: "16px", fontSize: 15, fontFamily: "Georgia,serif", border: `1px solid ${C.border}`, background: C.white, color: C.text, outline: "none", resize: "none", lineHeight: 1.65, marginBottom: 12 }}
            />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button className="btn-amber" onClick={recommend} disabled={loading || !input.trim()}
                style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {loading ? <><span className="spin" />Reading your situation...</> : "Recommend a program →"}
              </button>
            </div>

            {rec && (
              <div className="rec-card">
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                  <p className="mono" style={{ fontSize: 10, color: C.amber, letterSpacing: ".1em" }}>RECOMMENDATION</p>
                  <span className="mono" style={{ fontSize: 10, color: C.muted, letterSpacing: ".06em" }}>{rec.confidence === "high" ? "· high confidence" : "· moderate confidence"}</span>
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 400, marginBottom: 12 }}>{rec.headline}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.75, color: C.muted, marginBottom: 24 }}>{rec.what_to_expect}</p>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <a href={rec.url} className="btn-amber">{rec.cta} →</a>
                  <a href="/" className="btn-outline" style={{ fontSize: 11 }}>See all programs</a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* All programs */}
        <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${C.border}` }}>
          <p className="mono" style={{ fontSize: 10, color: C.muted, letterSpacing: ".12em", marginBottom: 20 }}>ALL PROGRAMS</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: C.border }}>
            {[
              ["Director's Defense", "/for-directors", "For daycare directors with citations", "$97/year"],
              ["Inspection Defense", "/inspection-defense", "For experienced operators managing appeals", "$197/year"],
              ["Mirror Bloom Logic", "/mirror-bloom", "For personal pattern recognition", "$47 challenge"],
              ["Naming Roots Lab", "/naming-roots", "For IP and brand name research", "$197/month"],
              ["Logic Rigor Pro", "/app", "For writing and argument training", "$12/month"],
              ["Roots Mode ℵ", "/roots", "For Hebrew language and morphology", "Free"],
            ].map(([name, url, desc, price]) => (
              <a key={name} href={url} style={{ background: C.white, padding: "16px 18px", textDecoration: "none", color: C.text, display: "block", transition: "background .15s" }}
                onMouseEnter={e => (e.currentTarget.style.background = C.soft)}
                onMouseLeave={e => (e.currentTarget.style.background = C.white)}>
                <div style={{ fontSize: 15, marginBottom: 4 }}>{name}</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{desc}</div>
                <div className="mono" style={{ fontSize: 11, color: C.amber }}>{price}</div>
              </a>
            ))}
          </div>
        </div>
      </div>

      <footer style={{ padding: "24px 48px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }}>
        <span className="mono" style={{ fontSize: 10, color: C.muted }}>LOGIC RIGOR — ELLARI VENTURES LLC — 2026</span>
        <a href="https://ellari.dev" className="mono" style={{ fontSize: 10, color: C.muted, textDecoration: "none" }}>Ellari Ventures →</a>
      </footer>
    </div>
  );
}
